import { Grid, GridItem } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import MarkdownWrapper from 'components/MarkdownWrapper/MarkdownWrapper';
import { useFormConfig } from 'config/formConfig/hooks';
import paths from 'constants/paths';
import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { getCompanyId, getInquiryIdSelector, isKycCompleted } from 'store/inquiryDetails/selectors';
import { ButtonComponent } from 'theme/components/Button';
import { HeadingComponent } from 'theme/components/Heading';
import ArrowRightIcon from 'theme/components/Icon/ArrowRightIcon';
import { ProgressSection } from 'theme/components/ProgressSection';
import { TextComponent } from 'theme/components/Text';
import { useTranslations } from 'utils/hooks/useTranslations';

import { useTriggerKyc } from '../useOperationProgressSections';

type AutoMergeActionProps = {
  isFirst?: boolean;
  isLast?: boolean;
  isCompleted?: boolean;
  isPending?: boolean;
  completedValue?: number;
  progressIncrement?: number;
  progressTotal?: number;
  id?: string;
};

export const AutoMergeAction = ({
  isFirst,
  isLast,
  isCompleted,
  completedValue,
  progressIncrement,
  progressTotal,
  id,
}: AutoMergeActionProps) => {
  const t = useTranslations('pages.inquiryDetails.dashboard.actions.autoMerge');
  const inquiryId = useSelector(getInquiryIdSelector);
  const companyId = useSelector(getCompanyId);
  const history = useHistory();
  const { selectedInquiryType } = useFormConfig();
  const isCrefoFactoring = selectedInquiryType === InquiryType.onlinefactoring;
  const { visible: triggerKycStepVisible } = useTriggerKyc();
  const hasKycCompleted = useSelector(isKycCompleted);

  const actionButtonDisabled = isCrefoFactoring && !hasKycCompleted && triggerKycStepVisible;

  return (
    <ProgressSection
      isFirst={isFirst}
      isLast={isLast}
      isCompleted={isCompleted}
      completedValue={completedValue}
      progressIncrement={progressIncrement}
      progressTotal={progressTotal}
      id={id}
    >
      <Grid templateColumns={['repeat(1, 1fr)', null, '2fr 1fr']} gap={12}>
        <GridItem>
          <HeadingComponent as="h4" color="brand.default" mb={2} variant="secondary">
            {t('title')}
          </HeadingComponent>

          <TextComponent mb={6}>
            <MarkdownWrapper text={t('description')} />
          </TextComponent>

          {!isCompleted && (
            <ButtonComponent
              onClick={() =>
                history.push(
                  paths.operation.inquiryDetails.companyDetails.associatedPersons(
                    inquiryId!,
                    companyId,
                  ),
                )
              }
              leftIcon={<ArrowRightIcon boxSize={6} display="block" />}
              variant="primary"
              disabled={actionButtonDisabled}
            >
              {t('action')}
            </ButtonComponent>
          )}
        </GridItem>
      </Grid>
    </ProgressSection>
  );
};
